import { useEffect, useId, useRef } from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';
import type { ModalProps } from './Modal.types';
import './Modal.scss';

export default function Modal({
  open, onClose, title, size = 'md', children, footer, closeOnOverlay = true, className, ariaLabel,
}: ModalProps) {
  const titleId = useId();
  const dialogRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    const t = window.setTimeout(() => {
      const node = dialogRef.current;
      if (!node) return;
      const focusable = node.querySelector<HTMLElement>('button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])');
      (focusable ?? node).focus();
    }, 10);
    return () => {
      document.body.style.overflow = prev;
      document.removeEventListener('keydown', onKey);
      window.clearTimeout(t);
    };
  }, [open, onClose]);

  if (!open) return null;
  return createPortal(
    <div
      className={['modal', `modal--${size}`, className || ''].filter(Boolean).join(' ')}
      onMouseDown={e => { if (closeOnOverlay && e.target === e.currentTarget) onClose(); }}
    >
      <div className="modal__overlay" aria-hidden="true" />
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={title ? titleId : undefined}
        aria-label={!title ? ariaLabel : undefined}
        tabIndex={-1}
        className="modal__dialog"
      >
        {title ? (
          <div className="modal__header">
            <h2 id={titleId} className="modal__title">{title}</h2>
            <button type="button" aria-label="Закрыть" className="modal__close" onClick={onClose}><X size={18} /></button>
          </div>
        ) : (
          <button type="button" aria-label="Закрыть" className="modal__close modal__close--floating" onClick={onClose}><X size={18} /></button>
        )}
        <div className="modal__body">{children}</div>
        {footer ? <div className="modal__footer">{footer}</div> : null}
      </div>
    </div>,
    document.body,
  );
}
